import { format, startOfWeek, endOfWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import { 
  ChevronLeft, 
  ChevronRight, 
  RefreshCw, 
  Calendar as CalendarIcon,
  ExternalLink,
  CalendarDays,
  CalendarRange,
  LayoutGrid
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type CalendarView = 'month' | 'week' | 'day';

interface CalendarHeaderProps {
  currentDate: Date;
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onNavigate: (direction: 'prev' | 'next') => void;
  onToday: () => void;
  onSync: () => void;
  syncing: boolean;
}

function getDateLabel(date: Date, view: CalendarView): string {
  switch (view) {
    case 'day':
      return format(date, 'd MMMM yyyy', { locale: nl });
    case 'week':
      const weekStart = startOfWeek(date, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
      return `${format(weekStart, 'd MMM', { locale: nl })} - ${format(weekEnd, 'd MMM yyyy', { locale: nl })}`;
    default:
      return format(date, 'MMMM yyyy', { locale: nl });
  }
}

export function CalendarHeader({ 
  currentDate,
  view,
  onViewChange,
  onNavigate, 
  onToday,
  onSync,
  syncing 
}: CalendarHeaderProps) {
  return (
    <div className="flex flex-col gap-4 mb-6">
      {/* Top row: Title + Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Title section */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-energy-green to-energy-blue">
            <CalendarIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Mijn Agenda</h1>
            <p className="text-sm text-muted-foreground">
              Microsoft Outlook synchronisatie
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={onSync}
            disabled={syncing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            Synchroniseer
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => window.open('https://outlook.office.com/calendar', '_blank')}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Outlook
          </Button>
        </div>
      </div>

      {/* Bottom row: View switcher + Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* View switcher */}
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
          <Button 
            variant={view === 'month' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewChange('month')}
            className={cn(
              "gap-1.5",
              view === 'month' && "bg-energy-green hover:bg-energy-green/90"
            )}
          >
            <LayoutGrid className="h-4 w-4" />
            Maand
          </Button>
          <Button 
            variant={view === 'week' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewChange('week')}
            className={cn(
              "gap-1.5",
              view === 'week' && "bg-energy-green hover:bg-energy-green/90"
            )}
          >
            <CalendarRange className="h-4 w-4" />
            Week
          </Button>
          <Button 
            variant={view === 'day' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewChange('day')}
            className={cn(
              "gap-1.5",
              view === 'day' && "bg-energy-green hover:bg-energy-green/90"
            )}
          >
            <CalendarDays className="h-4 w-4" />
            Dag
          </Button>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-1">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => onNavigate('prev')}
            className="h-9 w-9"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <div className="min-w-[200px] text-center">
            <span className="font-semibold text-lg capitalize">
              {getDateLabel(currentDate, view)}
            </span>
          </div>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => onNavigate('next')}
            className="h-9 w-9"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={onToday}
            className="ml-2"
          >
            Vandaag
          </Button>
        </div>
      </div>
    </div>
  );
}
